import React, { ReactNode, useEffect, useState } from 'react';

import Input from '../Input';
import useDebounce from '../../hooks/useDebounce';

const searchIconSvg = 'https://agendamento-images-nprd.telemedicinaeinstein.com.br/schedule-lib-assets/search.svg';
const ArrowRightIcon =
  'https://agendamento-images-nprd.telemedicinaeinstein.com.br/schedule-lib-assets/arrow-right.svg';

import * as S from './styles';
import Loading from '../Loading';

export type InputSearchProps = {
  onChange: (value: string) => void;
  onClickResult?: (value: string) => void;
  debounceTime?: number;
  placeholder?: string;
  results?: ResultProps;
  searchIcon?: boolean;
  onFocus?: (displayValue: string) => void;
  fullWidth?: boolean;
  label?: string;
  iconLeft?: ReactNode;
  clickOnIcon?: () => void;
  autoFocus?: boolean;
  error?: string;
  clearIcon?: boolean;
  rounded?: boolean;
  value?: string;
  handleChangeValue?: (value: string) => void;
  handleClearIcon?: () => void;
  onBlur?: () => void;
  testId?: string;
  disabled?: boolean;
  iconRight?: ReactNode;
};

export type ResultProps = { value: string; key: string }[] | [];

const InputSearch = ({
  onChange,
  placeholder,
  debounceTime,
  results = [],
  searchIcon = true,
  onClickResult = () => {},
  onFocus,
  fullWidth = false,
  label,
  iconLeft = false,
  clickOnIcon,
  error,
  autoFocus,
  clearIcon,
  rounded,
  value = '',
  onBlur = () => {},
  handleChangeValue = () => {},
  handleClearIcon = () => {},
  testId = '',
  disabled = false,
  iconRight
}: InputSearchProps) => {
  const [displayValue, setDisplayValue] = useState(value);
  const debounceChanged = useDebounce(onChange, debounceTime || 500);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(false);
  }, [results, error]);

  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  const handleChange = async (event: React.FormEvent<HTMLInputElement>) => {
    if (event.currentTarget.value.length > 0) setLoading(true);
    handleChangeValue(event.currentTarget.value);
    setDisplayValue(event.currentTarget.value);
    debounceChanged(event.currentTarget.value);
  };

  const iconInput = () => {
    if (iconLeft) return <>{iconLeft}</>;
    else if (searchIcon) return <img src={searchIconSvg} alt="" />;
    return null;
  };

  const handleIconRight = () => {
    if (iconRight) return iconRight;

    if (clearIcon && displayValue) {
      return (
        <div
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setDisplayValue('');
            handleClearIcon && handleClearIcon();
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            width="24"
            height="24"
            viewBox="0 0 24 24"
          >
            <defs>
              <clipPath id="clip-path">
                <rect id="Frame" width="24" height="24" transform="translate(334 192)" fill="#d5d5d6" />
              </clipPath>
            </defs>
            <g id="Apagar" transform="translate(-334 -192)" clipPath="url(#clip-path)">
              <g id="Icon" transform="translate(334.586 192.586)">
                <line
                  id="Line"
                  x1="12"
                  y1="12"
                  transform="translate(5.415 5.414)"
                  fill="none"
                  stroke="#d5d5d6"
                  strokeLinecap="round"
                  strokeWidth="2"
                />
                <line
                  id="Line-2"
                  data-name="Line"
                  x1="12"
                  y2="12"
                  transform="translate(5.415 5.414)"
                  fill="none"
                  stroke="#d5d5d6"
                  strokeLinecap="round"
                  strokeWidth="2"
                />
              </g>
            </g>
          </svg>
        </div>
      );
    }

    return null;
  };

  return (
    <S.Container hasResults={!!results.length} fullWidth={fullWidth} rounded={rounded}>
      <div className="input-wrapper">
        <Input
          icon={iconInput() ? iconInput() : null}
          clickOnIcon={clickOnIcon}
          value={displayValue}
          onChange={handleChange}
          placeholder={placeholder}
          label={label}
          onFocus={() => {
            if (onFocus) onFocus(displayValue);
          }}
          disabled={disabled}
          autoFocus={autoFocus}
          onBlur={onBlur}
          data-testid={testId}
          iconRight={handleIconRight()}
        />
      </div>

      {loading ? (
        <div className="loading-wrapper">
          <Loading loading />
        </div>
      ) : null}

      {results.length > 0 && !loading && (
        <S.Results>
          {results.map((item, index) => (
            <S.ResultName
              key={index + item.value}
              onClick={() => {
                onClickResult(item.value);
                setDisplayValue(item.value);
              }}
            >
              <p>{item.key}</p>
              <img src={ArrowRightIcon} alt="Arrow Icon" />
            </S.ResultName>
          ))}
        </S.Results>
      )}
    </S.Container>
  );
};

export default InputSearch;
